import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DateFilterPicker from './DateFilterPicker';
import { AccountingType, AttrType, ContactType, DateFilter, Item, LedgerReport, ReportFilter } from '../../../types';
import ButtonWithLoader from '../../widgets/components/ButtonWithLoader';
import { AccountPicker } from '../../masterdata/components/AccountPicker';
import ZToggleButtonGroup from '../../widgets/components/ToggleButtonGroup';
import { ContactPicker } from '../../masterdata/components/ContactPicker';
import { ItemPicker } from '../../masterdata/components/ItemPicker';
import { AccountCategory } from '../../../Constants';
import { getCurrency, getBusinessLocale, globalValue, useAppContext } from '../../apps/AppContext';
import { canUseModule, Edition, getEffectiveEdition } from '../../subscription/subscriptionAccess';
import { displayDateInBizTZ } from '../../utils/MiscFunctions';
import { BranchFilter } from '../../settings/BranchSettings';
import { toast } from 'react-toastify';
import { MultiSelectPicker } from '../../masterdata/components/AttributePicker';
import { ProjectPicker } from '../../projects/ProjectPicker';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import * as XLSXStyle from 'xlsx-js-style';
import { trackEvent, AnalyticsEvent } from '../../apps/analytics';
import dayjs from 'dayjs';
import { drawReportPdfHeader } from '../../utils/ExportTableToPDF';

// Module-level so AccountPicker's effect (keyed on this array) doesn't refetch every render.
// Credit cards (incl. Plaid-linked ones) have bank statements too.
const BANK_STATEMENT_ACCOUNT_CATEGORIES = [AccountCategory.Bank, AccountCategory.CreditCard];

export interface MandatoryField {
  field: keyof ReportFilter;
  label: string;
  validator?: (value: any) => boolean; // Custom validator function
}

export interface ReportFilterPanelProps {
  filterExpanded: boolean;
  selectedFilter: ReportFilter;
  onRunReport: () => void;
  loading: boolean;
  showDateFilter?: boolean;
  showYearOnly?: boolean;
  allowCustomDateRange?: boolean; // when showYearOnly, also let the user switch to a custom from/to range
  periodType?: 'periodic' | 'YTD' | 'BTD';
  showAccountFilter?: boolean;
  showBankFilter?: boolean;
  showContactPicker?: boolean;
  multiSelectContact?: boolean;
  showAccountingType?: boolean;
  allowMultiContact?: boolean;
  accountingType?: AccountingType;
  contactType?: ContactType;
  showBranchPicker?: boolean;
  showComparisonToggle?: boolean;
  mandatoryFields?: MandatoryField[]; // New prop for mandatory fields
  onAccountingTypeChange?: (newType: AccountingType) => void;
  onFilterChange: (newReportFilter: ReportFilter) => void;
  storageKey?: string;
  helpfulTip?: string; // Add this new prop
  showTagsFilter?: boolean; // Add this line
  showContactCategoryFilter?: boolean;
  showContactGroupFilter?: boolean;
  showItemFilter?: boolean;
  showItemCategoryFilter?: boolean;
  showItemGroupFilter?: boolean; // MBAI-451
  showProjectFilter?: boolean;
  showAgingBucketFilter?: boolean;
}

const LOCAL_STORAGE_PREFIX = 'ReportFilter_';

// Default validator functions
const defaultValidators = {
  contactFilter: (value: any) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  },
  accountFilter: (value: any) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  },
  branchId: (value: any) => value !== null && value !== undefined && value !== '',
  dateFilter: (value: any) => value !== null && value !== undefined,
  accountingType: (value: any) => value !== null && value !== undefined && value !== '',
};

export const ReportFilterPanel: React.FC<ReportFilterPanelProps> = ({
  filterExpanded,
  selectedFilter,
  onFilterChange,
  onRunReport,
  loading,
  showDateFilter = true,
  showYearOnly = false,
  allowCustomDateRange = false,
  periodType = 'periodic',
  showAccountFilter = true,
  showBankFilter = false,
  showContactPicker = false,
  multiSelectContact,
  showAccountingType = true,
  contactType,
  showBranchPicker,
  showComparisonToggle = false,
  accountingType,
  mandatoryFields = [], // Default to empty array
  onAccountingTypeChange,
  storageKey,
  helpfulTip,
  showTagsFilter = false,
  showContactCategoryFilter = false,
  showContactGroupFilter = false,
  showItemFilter = false,
  showItemCategoryFilter = false,
  showItemGroupFilter = false,
  showProjectFilter = false,
  showAgingBucketFilter = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(filterExpanded);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const currentFilterRef = useRef(selectedFilter);
  currentFilterRef.current = selectedFilter;
  const { subscriptionData, bizMaster } = useAppContext();
  const effectiveEdition: Edition = subscriptionData
    ? getEffectiveEdition(subscriptionData.currentPlan.edition as Edition, subscriptionData.currentPlan.status, subscriptionData.currentPlan.isTrial)
    : 'Free';
  const canShowBranchPicker = showBranchPicker && canUseModule('MultiBranch', effectiveEdition);
  const canShowProjectFilter = showProjectFilter && canUseModule('Projects', effectiveEdition);
  // Scope the cache key by bizId so switching businesses doesn't leak a stale
  // contact/account/date filter from one biz's saved report filter into another's.
  const localStorageKey = `${LOCAL_STORAGE_PREFIX}${bizMaster?.bizId || globalValue?.bizMaster?.bizId}_${storageKey}`;
  const location = useLocation();
  // Arriving with query params means the page was opened via a drilldown link
  // (e.g. accountId/from/to, contactId) — that filter is more specific than
  // whatever was last saved, so don't let the saved filter clobber it.
  const arrivedViaDrilldown = !!location.search;

  // Load selections from local storage on mount (branchId is never saved, so it won't be present)
  useEffect(() => {
    if (storageKey && !arrivedViaDrilldown) {
      const savedFilter = localStorage.getItem(localStorageKey);
      if (savedFilter) {
        const parsed = JSON.parse(savedFilter);
        delete parsed.branchId; // strip any stale branchId from old localStorage entries
        // Re-run relative filters (Today/ThisWeek/ThisMonth/...) through the DateFilter
        // constructor so they resolve against *now*, not the instant they were saved.
        // A plain JSON.parse keeps the label ("Today") but freezes the fromDate/toDate
        // that were computed back when the filter was last persisted — so a report
        // reopened on a later day silently queries the wrong day while still showing
        // "Today" as selected.
        if (parsed.dateFilter) {
          parsed.dateFilter = new DateFilter(parsed.dateFilter, periodType);
        }
        onFilterChange(parsed);
      }
    }
  }, [localStorageKey]);

  // Clear stale branchId when user doesn't have MultiBranch access
  useEffect(() => {
    if (!canShowBranchPicker && selectedFilter.branchId) {
      onFilterChange({ ...selectedFilter, branchId: undefined });
    }
  }, [canShowBranchPicker]);

  // Save selections to local storage whenever they change (branchId excluded — must be set each session)
  useEffect(() => {
    if (storageKey) {
      const { branchId: _b, ...filterToSave } = selectedFilter;
      localStorage.setItem(localStorageKey, JSON.stringify(filterToSave));
    }
  }, [localStorageKey, selectedFilter]);

  // Clear validation errors when filter values change
  useEffect(() => {
    if (Object.keys(validationErrors).length > 0) {
      const newErrors = { ...validationErrors };
      let hasChanges = false;

      mandatoryFields.forEach(({ field }) => {
        if (validationErrors[field as string]) {
          const validator = getValidator(field);
          const value = selectedFilter[field];
          if (validator(value)) {
            delete newErrors[field as string];
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setValidationErrors(newErrors);
      }
    }
  }, [selectedFilter, validationErrors, mandatoryFields]);

  const getValidator = (field: keyof ReportFilter) => {
    const mandatoryField = mandatoryFields.find(mf => mf.field === field);
    return mandatoryField?.validator || defaultValidators[field as keyof typeof defaultValidators] || (() => true);
  };

  const validateMandatoryFields = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    mandatoryFields.forEach(({ field, label, validator }) => {
      const fieldValidator = validator || getValidator(field);
      const value = currentFilterRef.current[field];

      if (!fieldValidator(value)) {
        errors[field as string] = `${label.toTranslatedDisplay()} ${'is required'.toTranslatedDisplay()}`;
        isValid = false;
      }
    });

    setValidationErrors(errors);

    if (!isValid) {
      // Show error toast with all missing fields
      const missingFields = Object.values(errors).join(', ');
      toast.error(`${'Please select the following required fields:'.toTranslatedDisplay()} ${missingFields}`);
      setIsExpanded(true); // Expand the panel to show the errors
    }

    return isValid;
  };

  const handleExpandClick = () => {
    setIsExpanded((prevState) => !prevState);
  };

  const handleFilterChange = (attribute: keyof ReportFilter, value: any) => {
    const updatedFilter = { ...selectedFilter, [attribute]: value };
    currentFilterRef.current = updatedFilter;
    onFilterChange(updatedFilter);
  };

  const handleRunReport = () => {
    if (validateMandatoryFields()) {
      trackEvent(AnalyticsEvent.ReportRun, storageKey ? { report: storageKey } : undefined);
      onRunReport();
      setIsExpanded(false); // Collapse filter panel after running
    }
  };

  const getFieldClassName = (fieldName: string) => {
    return validationErrors[fieldName] ? 'border-danger' : '';
  };

  const renderValidationError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="text-danger small mt-1">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  // Build a short summary of active filters for the collapsed view — only show non-empty values
  const collapsedSummary: { label: string; value: string }[] = [];
  const sel = 'selected'.toTranslatedDisplay();
  if (showDateFilter) {
    const df = selectedFilter?.dateFilter;
    if (df?.fromDate || df?.toDate) {
      const dateLabel = periodType === 'periodic'
        ? `${df?.fromDate ? displayDateInBizTZ(new Date(df.fromDate)) : '?'} – ${df?.toDate ? displayDateInBizTZ(new Date(df.toDate)) : '?'}`
        : `${'As of'.toTranslatedDisplay()} ${df?.toDate ? displayDateInBizTZ(new Date(df.toDate)) : '?'}`;
      collapsedSummary.push({ label: 'Date'.toTranslatedDisplay(), value: dateLabel });
    }
  }
  if (showAccountFilter && (selectedFilter?.accountFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Accounts'.toTranslatedDisplay(), value: `${selectedFilter.accountFilter!.length} ${sel}` });
  }
  if (showContactPicker) {
    const cnt = Array.isArray(selectedFilter?.contactFilter) ? selectedFilter.contactFilter.length : selectedFilter?.contactFilter ? 1 : 0;
    if (cnt > 0) collapsedSummary.push({ label: 'Contact'.toTranslatedDisplay(), value: `${cnt} ${sel}` });
  }
  if (showItemFilter && (selectedFilter?.itemFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Items'.toTranslatedDisplay(), value: `${selectedFilter.itemFilter!.length} ${sel}` });
  }
  if (showItemCategoryFilter && (selectedFilter?.itemCategoryFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Item Category'.toTranslatedDisplay(), value: `${selectedFilter.itemCategoryFilter!.length} ${sel}` });
  }
  if (showItemGroupFilter && (selectedFilter?.itemGroupFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Item Group'.toTranslatedDisplay(), value: `${selectedFilter.itemGroupFilter!.length} ${sel}` });
  }
  if (showTagsFilter && (selectedFilter?.tagsFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Tags'.toTranslatedDisplay(), value: `${selectedFilter.tagsFilter!.length} ${sel}` });
  }
  if (canShowProjectFilter && selectedFilter?.projectFilter) {
    collapsedSummary.push({ label: 'Project'.toTranslatedDisplay(), value: '1 selected' });
  }
  if (showContactCategoryFilter && (selectedFilter?.contactCategoryFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Category'.toTranslatedDisplay(), value: `${selectedFilter.contactCategoryFilter!.length} ${sel}` });
  }
  if (showContactGroupFilter && (selectedFilter?.contactGroupFilter?.length || 0) > 0) {
    collapsedSummary.push({ label: 'Group'.toTranslatedDisplay(), value: `${selectedFilter.contactGroupFilter!.length} ${sel}` });
  }
  if (canShowBranchPicker && selectedFilter.branchId) collapsedSummary.push({ label: 'Branch'.toTranslatedDisplay(), value: `1 ${sel}` });
  if (showAccountingType && selectedFilter.accountingType) collapsedSummary.push({ label: 'Type'.toTranslatedDisplay(), value: selectedFilter.accountingType });
  if (showComparisonToggle && selectedFilter.comparePreviousPeriod) collapsedSummary.push({ label: 'Compare'.toTranslatedDisplay(), value: 'Prior year'.toTranslatedDisplay() });
  if (showAgingBucketFilter) collapsedSummary.push({ label: 'Group by'.toTranslatedDisplay(), value: `${selectedFilter.agingBucketSize || 15} ${'Days'.toTranslatedDisplay()}` });

  return (
    <div className="card-body px-9 pt-6 pb-7">
      {/* ── Header bar ───────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-funnel-fill text-primary fs-5"></i>
          <span className="fw-semibold text-gray-700 fs-6">{'Filters'.toTranslatedDisplay()}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ButtonWithLoader
            className="btn-sm"
            label={'Run Report'.toTranslatedDisplay()}
            isLoading={loading}
            onClick={handleRunReport}
          />
          <button
            className="btn btn-sm btn-light-secondary d-flex align-items-center gap-1"
            onClick={handleExpandClick}
          >
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} fs-7`}></i>
            {isExpanded ? 'Collapse'.toTranslatedDisplay() : 'Expand'.toTranslatedDisplay()}
          </button>
        </div>
      </div>

      {/* ── Collapsed summary ────────────────────────────────────── */}
      {!isExpanded && (
        <div className="mt-3" style={{ cursor: 'pointer' }} onClick={handleExpandClick}>
          {collapsedSummary.length > 0 && (
            <div className="d-flex flex-wrap gap-2">
              {collapsedSummary.map(({ label, value }) => (
                <span key={label} className="badge badge-light-primary text-gray-700 fs-7 py-2 px-3">
                  <span className="fw-semibold text-gray-800">{label}:</span>{' '}{value}
                </span>
              ))}
            </div>
          )}
          {collapsedSummary.length === 0 && (
            <span className="text-muted fs-7">{'No filters applied — click to set filters.'.toTranslatedDisplay()}</span>
          )}
          {helpfulTip && (
            <div className="text-muted fs-8 mt-1">
              <i className="bi bi-lightbulb me-1"></i>{helpfulTip.toTranslatedDisplay()}
            </div>
          )}
        </div>
      )}

      {/* ── Expanded filter grid ─────────────────────────────────── */}
      {isExpanded && (
        <>
          <div className="row g-4 align-items-end mt-1">
            {showDateFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('dateFilter')}>
                  <DateFilterPicker
                    label={'Date'.toTranslatedDisplay()}
                    selectedDateFilter={selectedFilter.dateFilter}
                    periodType={periodType}
                    showYearOnly={showYearOnly}
                    allowCustomRange={allowCustomDateRange}
                    onChange={(dateFilter) => handleFilterChange('dateFilter', dateFilter)}
                  />
                </div>
                {renderValidationError('dateFilter')}
              </div>
            )}
            {showAccountFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('accountFilter')}>
                  <AccountPicker
                    label={'Account'.toTranslatedDisplay()}
                    tabIndex={0}
                    isMulti
                    selectedAccounts={selectedFilter.accountFilter}
                    onAccountChange={(accountIds) => handleFilterChange('accountFilter', accountIds)}
                  />
                </div>
                {renderValidationError('accountFilter')}
              </div>
            )}
            {showBankFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('accountFilter')}>
                  <AccountPicker
                    label={'Bank Account'.toTranslatedDisplay()}
                    tabIndex={0}
                    accountCategory={BANK_STATEMENT_ACCOUNT_CATEGORIES}
                    selectedAccounts={selectedFilter.accountFilter}
                    onAccountChange={(accountIds) => handleFilterChange('accountFilter', accountIds)}
                  />
                </div>
                {renderValidationError('accountFilter')}
              </div>
            )}
            {showContactPicker && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('contactFilter')}>
                  <ContactPicker
                    selectedContact={selectedFilter.contactFilter}
                    contactType={contactType}
                    tabIndex={0}
                    isMulti={multiSelectContact}
                    onContactChange={(contactIds) => handleFilterChange('contactFilter', contactIds)}
                  />
                </div>
                {renderValidationError('contactFilter')}
              </div>
            )}
            {canShowBranchPicker && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('branchId')}>
                  <BranchFilter
                    branchId={selectedFilter.branchId}
                    onBranchSelection={(branchId: string) => handleFilterChange('branchId', branchId)}
                  />
                </div>
                {renderValidationError('branchId')}
              </div>
            )}
            {showAccountingType && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('accountingType')}>
                  <ZToggleButtonGroup
                    label={'Accounting Type'.toTranslatedDisplay()}
                    optionOne={AccountingType.Accrual}
                    optionTwo={AccountingType.Cash}
                    selectedOption={selectedFilter.accountingType as AccountingType}
                    onOptionChange={(selected: string) => handleFilterChange('accountingType', selected)}
                  />
                </div>
                {renderValidationError('accountingType')}
              </div>
            )}
            {showTagsFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <div className={getFieldClassName('tagsFilter')}>
                  <MultiSelectPicker
                    categoryType={AttrType.Tags}
                    label={'Tags'.toTranslatedDisplay()}
                    showColor={true}
                    selectedAttributes={selectedFilter.tagsFilter}
                    onAttributeChange={(attributeIds: string[]) => handleFilterChange('tagsFilter', attributeIds)}
                  />
                </div>
                {renderValidationError('tagsFilter')}
              </div>
            )}
            {canShowProjectFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label">{'Project'.toTranslatedDisplay()}</label>
                <ProjectPicker
                  selectedProjectId={selectedFilter.projectFilter}
                  onProjectChange={(id) => handleFilterChange('projectFilter', id ?? undefined)}
                />
              </div>
            )}
            {showContactCategoryFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <MultiSelectPicker
                  categoryType={AttrType.ContactCategory}
                  label={'Contact Category'.toTranslatedDisplay()}
                  selectedAttributes={selectedFilter.contactCategoryFilter}
                  onAttributeChange={(ids: string[]) => handleFilterChange('contactCategoryFilter', ids)}
                />
              </div>
            )}
            {showContactGroupFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <MultiSelectPicker
                  categoryType={AttrType.ContactGroup}
                  label={'Contact Group'.toTranslatedDisplay()}
                  selectedAttributes={selectedFilter.contactGroupFilter}
                  onAttributeChange={(ids: string[]) => handleFilterChange('contactGroupFilter', ids)}
                />
              </div>
            )}
            {showItemFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-semibold text-gray-600 mb-2" style={{ fontSize: '0.85rem' }}>{'Item'.toTranslatedDisplay()}</label>
                <ItemPicker
                  isMulti
                  selectedItem={selectedFilter.itemFilter}
                  onItemChange={(items) => {
                    const ids = (Array.isArray(items) ? items : [items]).map((i: Item) => i.itemId);
                    handleFilterChange('itemFilter', ids);
                  }}
                />
              </div>
            )}
            {showItemCategoryFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <MultiSelectPicker
                  categoryType={AttrType.ItemCategory}
                  label={'Item Category'.toTranslatedDisplay()}
                  selectedAttributes={selectedFilter.itemCategoryFilter}
                  onAttributeChange={(ids: string[]) => handleFilterChange('itemCategoryFilter', ids)}
                />
              </div>
            )}
            {/* MBAI-451: item group filter for Stock Reports */}
            {showItemGroupFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <MultiSelectPicker
                  categoryType={AttrType.ItemGroup}
                  label={'Item Group'.toTranslatedDisplay()}
                  selectedAttributes={selectedFilter.itemGroupFilter}
                  onAttributeChange={(ids: string[]) => handleFilterChange('itemGroupFilter', ids)}
                />
              </div>
            )}
            {showAgingBucketFilter && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-semibold text-gray-600 mb-2" style={{ fontSize: '0.85rem' }}>{'Group By'.toTranslatedDisplay()}</label>
                <select
                  className="form-select"
                  value={selectedFilter.agingBucketSize || 15}
                  onChange={(e) => handleFilterChange('agingBucketSize', Number(e.target.value))}
                >
                  <option value={7}>{'7 Days'.toTranslatedDisplay()}</option>
                  <option value={15}>{'15 Days'.toTranslatedDisplay()}</option>
                  <option value={30}>{'30 Days'.toTranslatedDisplay()}</option>
                </select>
              </div>
            )}
            {showComparisonToggle && (
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-semibold text-gray-600 mb-2" style={{ fontSize: '0.85rem' }}>{'Prior Period Comparison'.toTranslatedDisplay()}</label>
                <div
                  className="d-flex align-items-center border rounded px-3"
                  style={{ height: '42px', cursor: 'pointer', backgroundColor: 'var(--bs-body-bg)' }}
                  onClick={() => handleFilterChange('comparePreviousPeriod', !selectedFilter.comparePreviousPeriod)}
                >
                  <div className="form-check form-switch mb-0 me-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      readOnly
                      checked={selectedFilter.comparePreviousPeriod || false}
                      style={{ width: '2.4em', height: '1.25em', cursor: 'pointer' }}
                    />
                  </div>
                  <span className="text-gray-700 fs-7">
                    {selectedFilter.comparePreviousPeriod ? 'Compare prior year'.toTranslatedDisplay() : 'Off'.toTranslatedDisplay()}
                  </span>
                </div>
              </div>
            )}
          </div>

          {helpfulTip && (
            <div className="d-flex justify-content-end mt-4">
              <div className="alert alert-info d-flex align-items-start mb-0" style={{ maxWidth: '420px' }}>
                <i className="bi bi-lightbulb text-info fs-4 me-3 flex-shrink-0"></i>
                <span className="text-primary fs-7">{helpfulTip.toTranslatedDisplay()}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const formatCurrency = (value?: number, currency?: string) => {
  const locale = getBusinessLocale();
  // currId may be stored as "INR-India Rupee" — extract the ISO code before the dash
  const rawCurr = (currency) ? currency : getCurrency();
  const curr = rawCurr.split('-')[0].trim();

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: curr,
  }).format(value || 0);
};

export function aggregateLedgerData(
  data: LedgerReport[],
  key: keyof LedgerReport
): LedgerReport[] {
  const aggregatedData = data.reduce((acc: Record<string, LedgerReport>, entry: LedgerReport) => {
    const groupKey = entry[key] as string;
    if (!groupKey) return acc;

    if (!acc[groupKey]) {
      acc[groupKey] = new LedgerReport({ ...entry });
    } else {
      acc[groupKey].debit = (acc[groupKey].debit || 0) + (entry.debit || 0);
      acc[groupKey].credit = (acc[groupKey].credit || 0) + (entry.credit || 0);
      acc[groupKey].debitYTD = (acc[groupKey].debitYTD || 0) + (entry.debitYTD || 0);
      acc[groupKey].creditYTD = (acc[groupKey].creditYTD || 0) + (entry.creditYTD || 0);
      acc[groupKey].amount = (acc[groupKey].amount || 0) + (entry.amount || 0);
      acc[groupKey].amountYTD = (acc[groupKey].amountYTD || 0) + (entry.amountYTD || 0);
    }
    return acc;
  }, {});

  return Object.values(aggregatedData);
}

export function aggregateLedgerDataByMonth(
  data: LedgerReport[],
  key: keyof LedgerReport
): LedgerReport[] {

  const aggregatedData = data.reduce(
    (acc: Record<string, LedgerReport>, entry: LedgerReport, idx: number) => {
      const groupKey = entry[key] as string;
      const monthNumber = entry.fiscalMonth as number | undefined;
      const monthName = monthNumber
        ? monthNumber
        : "no-month";

      const combinedKey = `${groupKey}_${monthName}`;
      if (!groupKey) {
        console.warn(`⚠️ Skipping entry at index ${idx} — missing key:`, entry);
        return acc;
      }



      if (!acc[combinedKey]) {
        acc[combinedKey] = new LedgerReport({ ...entry });
      } else {
        acc[combinedKey].debit = (acc[combinedKey].debit || 0) + (entry.debit || 0);
        acc[combinedKey].credit = (acc[combinedKey].credit || 0) + (entry.credit || 0);
        acc[combinedKey].debitYTD = (acc[combinedKey].debitYTD || 0) + (entry.debitYTD || 0);
        acc[combinedKey].creditYTD = (acc[combinedKey].creditYTD || 0) + (entry.creditYTD || 0);
        acc[combinedKey].amount = (acc[combinedKey].amount || 0) + (entry.amount || 0);
        acc[combinedKey].amountYTD = (acc[combinedKey].amountYTD || 0) + (entry.amountYTD || 0);
      }

      acc[combinedKey].fiscalMonth = monthNumber;

      return acc;
    },
    {}
  );

  const result = Object.values(aggregatedData);

  return result;
}

// ─── Prior period comparison helpers ───────────────────────────────────────

export function computePriorPeriodFilter(filter: ReportFilter): ReportFilter {
  const df = filter.dateFilter;
  if (!df?.fromDate || !df?.toDate) return filter;
  const priorFrom = new Date(df.fromDate);
  priorFrom.setFullYear(priorFrom.getFullYear() - 1);
  const priorTo = new Date(df.toDate);
  priorTo.setFullYear(priorTo.getFullYear() - 1);
  const priorDateFilter = new DateFilter({ predefinedDateFilter: 'Custom', fromDate: priorFrom, toDate: priorTo });
  return new ReportFilter({ ...filter, dateFilter: priorDateFilter, comparePreviousPeriod: false });
}

// ─── Drilldown helpers ──────────────────────────────────────────────────────
// Shared convention for drilldown links: from/to/predefinedDateFilter query params,
// read on the receiving report and used to auto-run instead of landing on a blank filter panel.

export function getDrilldownDateFilter(searchParams: URLSearchParams): DateFilter | undefined {
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  if (!from && !to) return undefined;
  // predefinedDateFilter must be explicit — the DateFilter constructor's updateDates()
  // call recomputes fromDate/toDate from this label and would otherwise discard from/to.
  const predefinedDateFilter = searchParams.get('predefinedDateFilter') || 'Custom';
  return new DateFilter({
    predefinedDateFilter,
    fromDate: from ? new Date(from) : undefined,
    toDate: to ? new Date(to) : undefined,
  });
}

// ─── Aging bucket helpers ───────────────────────────────────────────────────
// Buckets are always 3 fixed-width ranges plus an overflow bucket, e.g. for
// bucketSize=15: 1-15, 16-30, 31-45, >45. For bucketSize=30: 1-30, 31-60, 61-90, >90.

export function getAgingBucketLabels(bucketSize: number): [string, string, string, string] {
  const days = 'Days'.toTranslatedDisplay();
  return [
    `1-${bucketSize} ${days}`,
    `${bucketSize + 1}-${bucketSize * 2} ${days}`,
    `${bucketSize * 2 + 1}-${bucketSize * 3} ${days}`,
    `> ${bucketSize * 3} ${days}`,
  ];
}

export function getAgingBucketIndex(daysOverdue: number, bucketSize: number): 0 | 1 | 2 | 3 {
  if (daysOverdue <= bucketSize) return 0;
  if (daysOverdue <= bucketSize * 2) return 1;
  if (daysOverdue <= bucketSize * 3) return 2;
  return 3;
}

export function getPeriodLabel(filter: ReportFilter, isYTD = false): string {
  const df = filter.dateFilter;
  if (!df) return '';
  const to = df.toDate ? displayDateInBizTZ(new Date(df.toDate)) : '';
  const from = df.fromDate ? displayDateInBizTZ(new Date(df.fromDate)) : '';
  if (isYTD) return to ? `${'As of'.toTranslatedDisplay()} ${to}` : '';
  return from && to ? `${from} – ${to}` : to;
}

// ─── FinancialReportTable ───────────────────────────────────────────────────

export interface FinancialReportRow {
  label?: string;
  amount?: number;
  compAmount?: number;
  isSection?: boolean;   // Grey section header — no amounts, uppercase label
  isHighlight?: boolean; // Subtotal row — bold with border lines
  isSummary?: boolean;   // Grand total row — bold with coloured background
  isBolded?: boolean;    // Bold only (e.g. beginning/ending cash balance)
  isHeading?: boolean;   // Category heading — no amounts shown (cash flow)
  accountId?: string;    // For drilldown navigation to Account Transactions
}

interface FinancialReportTableProps {
  rows: FinancialReportRow[];
  currentPeriodLabel?: string;
  priorPeriodLabel?: string;
  showComparison?: boolean;
  title?: string;
  onRowClick?: (row: FinancialReportRow) => void;
}

export const FinancialReportTable: React.FC<FinancialReportTableProps> = ({
  rows,
  currentPeriodLabel = 'Current Period'.toTranslatedDisplay(),
  priorPeriodLabel = 'Prior Period'.toTranslatedDisplay(),
  showComparison = false,
  title = 'Report'.toTranslatedDisplay(),
  onRowClick,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const colSpan = showComparison ? 4 : 2;

  // Filter rows by search term — section/heading/summary rows are always kept so
  // structure is preserved; only detail rows whose label doesn't match are hidden.
  const visibleRows = searchTerm.trim()
    ? rows.filter(r =>
        r.isSection || r.isHeading || r.isSummary || r.isBolded || r.isHighlight ||
        (r.label || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : rows;

  const renderAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return formatCurrency(amount);
  };

  const renderChange = (amount?: number, compAmount?: number) => {
    if (amount === undefined || compAmount === undefined) return null;
    const change = amount - compAmount;
    const isPositive = change >= 0;
    const absPct = compAmount !== 0
      ? Math.abs((change / Math.abs(compAmount)) * 100).toFixed(1)
      : null;
    return (
      <span className={isPositive ? 'text-success' : 'text-danger'}>
        {isPositive ? '+' : ''}{formatCurrency(change)}
        {absPct !== null && (
          <span className="text-muted ms-1 fs-8">({absPct}%)</span>
        )}
      </span>
    );
  };

  // Format a number with locale thousands separators but WITHOUT a currency symbol.
  // jsPDF's built-in Helvetica font cannot render non-ASCII glyphs (e.g. ₹, €),
  // so we must strip the symbol before sending values to the PDF exporter.
  const formatNum = (value?: number): string => {
    if (value === undefined || value === null) return '';
    return new Intl.NumberFormat(getBusinessLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const changePct = (amount?: number, compAmount?: number): string => {
    if (amount === undefined || compAmount === undefined) return '';
    if (compAmount === 0) return 'N/A';
    const diff = amount - compAmount;
    return `${diff >= 0 ? '+' : ''}${((diff / Math.abs(compAmount)) * 100).toFixed(1)}%`;
  };

  // ── PDF export — styled per row type using jspdf-autotable cell objects ──
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: showComparison ? 'landscape' : 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header block — business logo/name, report title, selected period, generated timestamp
    const cursorY = drawReportPdfHeader(doc, title, currentPeriodLabel);

    // Column headers
    const headers = [
      'Account'.toTranslatedDisplay(),
      currentPeriodLabel,
      ...(showComparison ? [priorPeriodLabel, 'Change %'.toTranslatedDisplay()] : []),
    ];

    // Build body: each cell is an autotable cell object so we can style per row
    const body = rows.map(r => {
      const isStructural = r.isSection || r.isHeading;
      const isBold = r.isSection || r.isHeading || r.isSummary || r.isHighlight || r.isBolded;
      const fill =
        r.isSummary  ? [232, 247, 255] as [number,number,number] :
        r.isHighlight ? [250, 251, 252] as [number,number,number] :
        r.isBolded   ? [248, 249, 250] as [number,number,number] :
        r.isSection  ? [245, 248, 250] as [number,number,number] :
        [255, 255, 255] as [number,number,number];

      const labelIndent = (!r.isSection && !r.isHeading && !r.isSummary && !r.isHighlight && !r.isBolded)
        ? '    ' : '';  // 4-space indent for regular rows

      const labelContent = r.isSection
        ? (r.label || '').toUpperCase()
        : (labelIndent + (r.label || ''));

      const cellStyle = (halign: 'left' | 'right' = 'right') => ({
        styles: { fillColor: fill, fontStyle: isBold ? 'bold' : 'normal', halign },
      });

      const amountCell = (val?: number) =>
        isStructural
          ? { content: '', styles: { fillColor: fill } }
          : { content: val !== undefined ? formatNum(val) : '', ...cellStyle() };

      const pctCell = (a?: number, c?: number) =>
        isStructural
          ? { content: '', styles: { fillColor: fill } }
          : { content: changePct(a, c), ...cellStyle() };

      return [
        { content: labelContent, ...cellStyle('left') },
        amountCell(r.amount),
        ...(showComparison ? [amountCell(r.compAmount), pctCell(r.amount, r.compAmount)] : []),
      ];
    });

    autoTable(doc, {
      theme: 'grid',
      head: [headers],
      body,
      headStyles: {
        fillColor: [46, 64, 87],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      styles: { fontSize: 10, cellPadding: 2 },
      startY: cursorY + 2,
      didParseCell: (hookData: any) => {
        // Red text for negative amounts in body
        if (hookData.cell.section === 'body' && hookData.column.index > 0) {
          const raw = hookData.cell.raw;
          const content = typeof raw === 'object' ? raw?.content : raw;
          if (typeof content === 'string') {
            const num = parseFloat(content.replace(/[^0-9.\-]/g, ''));
            if (!isNaN(num) && num < 0) hookData.cell.styles.textColor = [192, 0, 0];
          }
        }
      },
    });

    // Write page footers after autoTable has finished so total page count is final
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 8,
        { align: 'center' }
      );
      doc.setTextColor(0, 0, 0);
    }

    doc.save(`${title}.pdf`);
  };

  // ── Excel export — styled per row type using xlsx-js-style ───────────────
  const handleExportExcel = () => {
    const bizDetails = (globalValue as any)?.bizMaster?.bizDetails;
    const executionTime = dayjs().format('DD-MMM-YYYY hh:mm A');

    // Meta header rows
    const metaRows: string[][] = [];
    if (bizDetails?.bizName) metaRows.push([bizDetails.bizName]);
    if (bizDetails?.address) metaRows.push([bizDetails.address]);
    metaRows.push([title]);
    if (currentPeriodLabel) metaRows.push([currentPeriodLabel]);
    metaRows.push([`Generated: ${executionTime}`]);
    metaRows.push([]); // blank spacer

    const colHeaders = [
      'Account'.toTranslatedDisplay(),
      currentPeriodLabel,
      ...(showComparison ? [priorPeriodLabel, 'Change'.toTranslatedDisplay()] : []),
    ];
    const colCount = colHeaders.length;

    // Per-row fill colours (RGB hex for xlsx-js-style)
    const fillFor = (r: FinancialReportRow): string | null => {
      if (r.isSummary)  return 'E8F7FF';
      if (r.isHighlight) return 'FAFBFC';
      if (r.isBolded)   return 'F8F9FA';
      if (r.isSection)  return 'F5F8FA';
      return null;
    };

    const isBold = (r: FinancialReportRow) =>
      !!(r.isSection || r.isHeading || r.isSummary || r.isHighlight || r.isBolded);

    const makeCell = (
      value: string | number,
      bold: boolean,
      halign: 'left' | 'right' | 'center',
      fillHex: string | null,
      isHeader = false
    ): XLSXStyle.CellObject => ({
      v: value,
      t: typeof value === 'number' ? 'n' : 's',
      s: {
        font: { bold },
        alignment: { horizontal: halign, wrapText: false },
        ...(fillHex ? { fill: { fgColor: { rgb: fillHex }, patternType: 'solid' } } : {}),
        ...(isHeader ? { fill: { fgColor: { rgb: '2E4057' }, patternType: 'solid' }, font: { bold: true, color: { rgb: 'FFFFFF' } } } : {}),
      },
    });

    // Header row styling
    const headerRow: XLSXStyle.CellObject[] = colHeaders.map(h =>
      makeCell(h, true, 'center', null, true)
    );

    // Data rows
    const dataRows: XLSXStyle.CellObject[][] = rows.map(r => {
      const fill = fillFor(r);
      const bold = isBold(r);
      const isStructural = r.isSection || r.isHeading;
      const labelText = r.isSection ? (r.label || '').toUpperCase() : (r.label || '');
      const labelIndent = (!r.isSection && !r.isHeading && !r.isSummary && !r.isHighlight && !r.isBolded)
        ? '    ' + labelText : labelText;

      const amountVal = (v?: number) =>
        isStructural ? makeCell('', bold, 'right', fill) : makeCell(v !== undefined ? formatNum(v) : '', bold, 'right', fill);

      const changeVal = (a?: number, c?: number) => {
        if (isStructural) return makeCell('', bold, 'right', fill);
        const diff = a !== undefined && c !== undefined ? a - c : undefined;
        const pct = changePct(a, c);
        const text = diff !== undefined ? `${diff >= 0 ? '+' : ''}${formatNum(diff)} (${pct})` : '';
        const isNeg = diff !== undefined && diff < 0;
        const cell = makeCell(text, bold, 'right', fill);
        if (isNeg) (cell.s as any).font = { ...(cell.s as any).font, color: { rgb: 'C00000' } };
        return cell;
      };

      return [
        makeCell(labelIndent, bold, 'left', fill),
        amountVal(r.amount),
        ...(showComparison ? [amountVal(r.compAmount), changeVal(r.amount, r.compAmount)] : []),
      ];
    });

    // Assemble sheet from array-of-arrays manually so we can apply cell objects
    const allRows: XLSXStyle.CellObject[][] = [
      // meta rows as plain text
      ...metaRows.map(mr => [makeCell(mr[0] || '', false, 'left', null)]),
      headerRow,
      ...dataRows,
    ];

    const ws: XLSXStyle.WorkSheet = {};
    allRows.forEach((row, ri) => {
      row.forEach((cell, ci) => {
        const addr = XLSXStyle.utils.encode_cell({ r: ri, c: ci });
        ws[addr] = cell;
      });
    });
    ws['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: allRows.length - 1, c: colCount - 1 } });

    // Column widths
    ws['!cols'] = [
      { wch: 40 }, // label column
      { wch: 18 }, // current period
      ...(showComparison ? [{ wch: 18 }, { wch: 24 }] : []),
    ];

    // Freeze header rows (meta + 1 column header row)
    ws['!freeze'] = { xSplit: 0, ySplit: metaRows.length + 1 };

    const safeSheetName = title.replace(/[\\/*?[\]:]/g, '').slice(0, 31) || 'Report';
    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, safeSheetName);
    XLSXStyle.writeFile(wb, `${title}.xlsx`);
  };

  if (rows.length === 0) {
    return (
      <div className="text-center text-muted py-10">
        <i className="bi bi-bar-chart fs-2x mb-3 d-block"></i>
        {'No data — run the report to see results.'.toTranslatedDisplay()}
      </div>
    );
  }

  return (
    <div>
      {/* ── Toolbar row — matches DataTable pattern ─────────────── */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="d-flex align-items-center">
          <input
            type="text"
            className="form-control me-3"
            placeholder={'Search'.toTranslatedDisplay()}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ height: '43px', minWidth: '100px', maxWidth: '250px' }}
          />
          {showComparison && priorPeriodLabel && (
            <span className="badge badge-light-info fs-7 py-2 px-3">
              {'Comparing:'.toTranslatedDisplay()} {priorPeriodLabel}
            </span>
          )}
        </div>
        <div className="text-end">
          <button
            className="btn btn-sm btn-secondary export-btn"
            onClick={handleExportPDF}
            title={'Export as PDF'.toTranslatedDisplay()}
          >
            <i className="fas fa-file-pdf"></i>
            <span className="export-text">{'Export as PDF'.toTranslatedDisplay()}</span>
          </button>
          <button
            className="ms-2 btn btn-sm btn-secondary export-btn"
            onClick={handleExportExcel}
            title={'Export as Excel'.toTranslatedDisplay()}
          >
            <i className="fas fa-file-excel"></i>
            <span className="export-text">{'Export as Excel'.toTranslatedDisplay()}</span>
          </button>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div className="table-responsive">
        <table className="table table-borderless w-100 financial-report-table" style={{ borderCollapse: 'separate', borderSpacing: '0 1px' }}>
          <thead className="fw-bold bg-secondary">
            <tr>
              <th className="p-2 ps-4" style={{ width: showComparison ? '40%' : '60%' }}></th>
              <th className="p-2 text-end pe-3">{currentPeriodLabel}</th>
              {showComparison && (
                <>
                  <th className="p-2 text-end pe-3">{priorPeriodLabel}</th>
                  <th className="p-2 text-end pe-3">{'Change'.toTranslatedDisplay()}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, idx) => {
              if (row.isSection) {
                return (
                  <tr key={idx}>
                    <td
                      colSpan={colSpan}
                      className="py-3 ps-4 fw-bold fs-7 text-uppercase fr-row-section"
                    >
                      {row.label}
                    </td>
                  </tr>
                );
              }

              if (row.isHeading) {
                return (
                  <tr key={idx} className="fr-row-heading">
                    <td className="py-2 ps-4 fw-bold text-gray-800">{row.label}</td>
                    <td colSpan={showComparison ? 3 : 1} className="pe-3"></td>
                  </tr>
                );
              }

              if (row.isSummary) {
                return (
                  <tr key={idx} className="fr-row-summary">
                    <td className="py-3 ps-4 fw-bolder text-gray-900">{row.label}</td>
                    <td className="py-3 text-end fw-bolder text-gray-900 pe-3">{renderAmount(row.amount)}</td>
                    {showComparison && (
                      <>
                        <td className="py-3 text-end fw-bolder text-gray-600 pe-3">{renderAmount(row.compAmount)}</td>
                        <td className="py-3 text-end fw-bolder pe-3">{renderChange(row.amount, row.compAmount)}</td>
                      </>
                    )}
                  </tr>
                );
              }

              if (row.isHighlight) {
                return (
                  <tr key={idx} className="fr-row-highlight">
                    <td className="py-2 ps-4 fw-bold text-gray-800">{row.label}</td>
                    <td className="py-2 text-end fw-bold text-gray-800 pe-3">{renderAmount(row.amount)}</td>
                    {showComparison && (
                      <>
                        <td className="py-2 text-end fw-bold text-gray-500 pe-3">{renderAmount(row.compAmount)}</td>
                        <td className="py-2 text-end fw-bold pe-3">{renderChange(row.amount, row.compAmount)}</td>
                      </>
                    )}
                  </tr>
                );
              }

              if (row.isBolded) {
                return (
                  <tr key={idx} className="fr-row-bolded">
                    <td className="py-3 ps-4 fw-bold text-gray-800">{row.label}</td>
                    <td className="py-3 text-end fw-bold text-gray-800 pe-3">{renderAmount(row.amount)}</td>
                    {showComparison && (
                      <>
                        <td className="py-3 text-end fw-bold text-gray-500 pe-3">{renderAmount(row.compAmount)}</td>
                        <td className="py-3 text-end fw-bold pe-3">{renderChange(row.amount, row.compAmount)}</td>
                      </>
                    )}
                  </tr>
                );
              }

              // Regular account row — clickable when accountId + onRowClick provided
              const isDrillable = !!(onRowClick && row.accountId);
              return (
                <tr
                  key={idx}
                  className={`fr-row-regular${isDrillable ? ' cursor-pointer text-hover-primary' : ''}`}
                  onClick={isDrillable ? () => onRowClick!(row) : undefined}
                  title={isDrillable ? 'Click to view transactions'.toTranslatedDisplay() : undefined}
                >
                  <td className="py-2 ps-8 text-gray-700 fs-6">
                    {row.label}
                    {isDrillable && <i className="bi bi-arrow-right-short ms-1 text-muted fs-8 opacity-50" />}
                  </td>
                  <td className="py-2 text-end text-gray-800 pe-3">{renderAmount(row.amount)}</td>
                  {showComparison && (
                    <>
                      <td className="py-2 text-end text-gray-500 pe-3">
                        {row.compAmount !== undefined ? renderAmount(row.compAmount) : '—'}
                      </td>
                      <td className="py-2 text-end pe-3">{renderChange(row.amount, row.compAmount)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};